<template>
  <!-- 科室管理页：普通后台暖米白主题，禁止 el-container 包整页 -->
  <div class="app-container department-page">
    <!-- 查询区 -->
    <el-form :inline="true" :model="queryForm" size="small" class="search-form" @submit.native.prevent>
      <el-form-item label="关键字">
        <el-input
          v-model="queryForm.keyword"
          placeholder="科室名称 / 负责人"
          clearable
          style="width: 200px"
          @keyup.enter.native="handleSearch"
        />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
        <el-button icon="el-icon-refresh-right" @click="handleReset">重置</el-button>
      </el-form-item>
    </el-form>

    <!-- 操作栏 -->
    <div class="toolbar">
      <el-button type="primary" icon="el-icon-plus" size="small" @click="openCreate">新增科室</el-button>
      <el-button
        type="success"
        icon="el-icon-download"
        size="small"
        :loading="importing"
        @click="handleImport"
      >从台账导入</el-button>
      <el-button
        type="warning"
        icon="el-icon-download"
        size="small"
        :loading="exporting"
        @click="handleExportAll"
      >导出Excel</el-button>
      <span class="toolbar-tip">从设备台账「使用科室」批量导入存量科室名，已存在的自动跳过</span>
    </div>

    <!-- 列表 -->
    <el-table v-loading="loading" :data="list" border size="small" class="dept-table" @selection-change="onSelectionChange">
      <el-table-column type="selection" width="45" align="center" />
      <el-table-column type="index" label="序号" width="55" align="center" />
      <el-table-column prop="name" label="科室名称" min-width="160" align="center" show-overflow-tooltip />
      <el-table-column prop="manager" label="负责人" width="110" align="center" show-overflow-tooltip>
        <template slot-scope="scope">{{ scope.row.manager || '-' }}</template>
      </el-table-column>
      <el-table-column prop="contact" label="联系方式" width="150" align="center" show-overflow-tooltip>
        <template slot-scope="scope">
          <!-- 后端统一返回脱敏值（138****6688）；点眼睛单独查看完整值（记审计日志） -->
          <span>{{ scope.row.contact || '-' }}</span>
          <el-button
            v-if="scope.row.contact"
            type="text"
            size="mini"
            icon="el-icon-view"
            class="contact-eye"
            @click="revealContact(scope.row)"
          />
        </template>
      </el-table-column>
      <el-table-column prop="location" label="位置" min-width="140" align="center" show-overflow-tooltip>
        <template slot-scope="scope">{{ scope.row.location || '-' }}</template>
      </el-table-column>
      <el-table-column prop="remark" label="备注" min-width="160" align="center" show-overflow-tooltip>
        <template slot-scope="scope">{{ scope.row.remark || '-' }}</template>
      </el-table-column>
      <el-table-column label="创建时间" width="160" align="center">
        <template slot-scope="scope">{{ formatTime(scope.row.created_at) }}</template>
      </el-table-column>
      <el-table-column label="操作" width="140" align="center">
        <template slot-scope="scope">
          <el-button type="text" size="small" @click="openEdit(scope.row)">编辑</el-button>
          <el-button type="text" size="small" class="danger-link" @click="handleDelete(scope.row)">删除</el-button>
        </template>
      </el-table-column>
      <template slot="empty">
        <div class="table-empty">{{ loading ? '加载中…' : '暂无科室数据，可点「从台账导入」初始化' }}</div>
      </template>
    </el-table>

    <!-- 分页 -->
    <el-pagination
      class="pager"
      layout="total, sizes, prev, pager, next"
      :current-page.sync="page"
      :page-size.sync="pageSize"
      :page-sizes="[10, 20, 50]"
      :total="total"
      @current-change="fetchList"
      @size-change="onSizeChange"
    />

    <!-- 新增/编辑对话框 -->
    <el-dialog :title="editing ? '编辑科室' : '新增科室'" :visible.sync="dialogVisible" width="520px">
      <!-- 改名会级联同步各业务表，打开编辑框且改动名称后给出黄色警示 -->
      <el-alert
        v-if="editing && nameChanged"
        type="warning"
        :closable="false"
        show-icon
        class="rename-alert"
        title="修改科室名称将同步更新台账/盘点/报告提醒/报修中的全部历史记录，请确认名称无误"
      />
      <el-form ref="form" :model="form" :rules="rules" label-width="90px" size="small">
        <el-form-item label="科室名称" prop="name">
          <el-input v-model="form.name" placeholder="请输入科室名称" maxlength="50" />
        </el-form-item>
        <el-form-item label="负责人" prop="manager">
          <el-input v-model="form.manager" placeholder="选填" maxlength="50" />
        </el-form-item>
        <el-form-item label="联系方式" prop="contact">
          <el-input v-model="form.contact" placeholder="留空保持原值不变；输入新值则覆盖" maxlength="50" />
        </el-form-item>
        <el-form-item label="位置" prop="location">
          <el-input v-model="form.location" placeholder="选填" maxlength="100" />
        </el-form-item>
        <el-form-item label="备注" prop="remark">
          <el-input v-model="form.remark" type="textarea" :rows="2" placeholder="选填" maxlength="255" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button size="small" @click="dialogVisible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="saving" @click="handleSubmit">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import departmentApi from '../api/department'
import { Message, MessageBox } from 'element-ui'

export default {
  name: 'DepartmentList',
  data() {
    return {
      queryForm: { keyword: '' },
      list: [],
      loading: false,
      page: 1,
      pageSize: 20,
      total: 0,
      // 导入
      importing: false,
      // 复选框选中行（预留：当前导出为全部导出）
      selectedRows: [],
      // 导出
      exporting: false,
      // 新增/编辑
      dialogVisible: false,
      editing: false,
      saving: false,
      originalName: '',
      form: { id: null, name: '', manager: '', contact: '', location: '', remark: '' },
      rules: {
        name: [{ required: true, message: '请输入科室名称', trigger: 'blur' }],
      },
    }
  },
  computed: {
    // 编辑时是否改了名称（用于顶部黄色警示）
    nameChanged() {
      return this.editing && this.form.name !== this.originalName
    },
  },
  mounted() {
    this.fetchList()
  },
  methods: {
    async fetchList() {
      this.loading = true
      try {
        const params = { page: this.page, page_size: this.pageSize }
        if (this.queryForm.keyword) params.keyword = this.queryForm.keyword
        const res = await departmentApi.listDepts(params)
        const d = res.data || {}
        this.list = d.results || []
        this.total = d.count || 0
      } catch (e) {
        // 透出后端真实错误
        const err = e.response && e.response.data
        const msg = (err && (err.detail || err.non_field_errors)) || '科室列表加载失败'
        Message.error(Array.isArray(msg) ? msg.join('；') : msg)
        this.list = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.page = 1
      this.fetchList()
    },
    handleReset() {
      this.queryForm.keyword = ''
      this.page = 1
      this.fetchList()
    },
    onSizeChange(size) {
      this.pageSize = size
      this.page = 1
      this.fetchList()
    },
    onSelectionChange(rows) {
      this.selectedRows = rows
    },
    // ---- 查看完整联系方式（后端记审计日志）----
    async revealContact(row) {
      try {
        const res = await departmentApi.revealContact(row.id)
        const contact = (res.data && res.data.contact) || '（未填写）'
        await MessageBox.alert(
          `${row.name}：${contact}`,
          '完整联系方式',
          { confirmButtonText: '关闭', type: 'info' }
        )
      } catch (e) {
        const err = e.response && e.response.data
        const msg = (err && (err.detail || err.non_field_errors)) || '查看失败'
        Message.error(msg)
      }
    },
    // ---- 导出Excel（全部导出，忽略当前查询条件）----
    // ⚠️ 前端生成 xlsx 的两种方式（XLSX.writeFile / 手动 Blob + xlsx MIME）在本机浏览器
    // 都被下载管理器拦截（25.3KB - 失败 - 下载错误）。改为与资质 ZIP 导出同模式：
    // 后端 openpyxl 生成文件 → axios blob 拉取（带 Token）→ octet-stream 落盘。
    async handleExportAll() {
      this.exporting = true
      try {
        const res = await departmentApi.exportExcel()
        const blob = new Blob([res.data], { type: 'application/octet-stream' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.style.display = 'none'
        a.href = url
        a.download = `科室清单_${this.formatDateForFile(new Date())}.xlsx`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        setTimeout(() => URL.revokeObjectURL(url), 60000)
        Message.success('导出成功')
      } catch (e) {
        // responseType:blob 时后端 JSON 错误被包成 Blob，解析出真实原因
        let msg = '导出失败'
        if (e.response && e.response.data instanceof Blob) {
          try {
            const text = await e.response.data.text()
            const j = JSON.parse(text)
            msg = j.detail || msg
          } catch (err) { /* 保持默认提示 */ }
        } else {
          const err = e.response && e.response.data
          msg = (err && (err.detail || err.non_field_errors)) || msg
        }
        Message.error(msg)
      } finally {
        this.exporting = false
      }
    },
    formatDateForFile(d) {
      const p = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}_${p(d.getHours())}${p(d.getMinutes())}`
    },
    // ---- 从台账导入 ----
    async handleImport() {
      try {
        await MessageBox.confirm(
          '将从设备台账「使用科室」批量导入存量科室名，已存在的自动跳过。是否继续？',
          '从台账导入',
          { type: 'info' }
        )
      } catch (e) {
        return // 用户取消
      }
      this.importing = true
      try {
        const res = await departmentApi.importFromEquipment()
        const d = res.data || {}
        Message.success(`导入完成：新导入 ${d.imported || 0} 个，已存在 ${d.existed || 0} 个`)
        this.fetchList()
      } catch (e) {
        const err = e.response && e.response.data
        const msg = (err && (err.detail || err.non_field_errors)) || '导入失败'
        Message.error(Array.isArray(msg) ? msg.join('；') : msg)
      } finally {
        this.importing = false
      }
    },
    // ---- 新增/编辑 ----
    openCreate() {
      this.editing = false
      this.originalName = ''
      this.form = { id: null, name: '', manager: '', contact: '', location: '', remark: '' }
      this.dialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.clearValidate()
      })
    },
    openEdit(row) {
      this.editing = true
      this.originalName = row.name || ''
      this.form = {
        id: row.id,
        name: row.name || '',
        manager: row.manager || '',
        // 列表返回的是脱敏值（138****6688），不回填；留空=保持原值，输入新值=覆盖（后端同样有保护）
        contact: '',
        location: row.location || '',
        remark: row.remark || '',
      }
      this.dialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.clearValidate()
      })
    },
    async handleSubmit() {
      try {
        await this.$refs.form.validate()
      } catch (e) {
        return
      }
      this.saving = true
      try {
        const payload = {
          name: (this.form.name || '').trim(),
          manager: this.form.manager || '',
          contact: this.form.contact || '',
          location: this.form.location || '',
          remark: this.form.remark || '',
        }
        if (this.editing) {
          const res = await departmentApi.updateDept(this.form.id, payload)
          const refs = (res.data && res.data.renamed_refs) || 0
          Message.success(refs > 0
            ? `保存成功，已同步 ${refs} 条历史记录`
            : '保存成功')
        } else {
          await departmentApi.createDept(payload)
          Message.success('新增成功')
        }
        this.dialogVisible = false
        this.fetchList()
      } catch (e) {
        const err = e.response && e.response.data
        const msg = (err && (err.detail || err.non_field_errors)) || '保存失败'
        Message.error(Array.isArray(msg) ? msg.join('；') : msg)
      } finally {
        this.saving = false
      }
    },
    // ---- 删除（逻辑删除）----
    async handleDelete(row) {
      try {
        await MessageBox.confirm(
          `确定删除科室「${row.name}」？逻辑删除后可在列表中恢复`,
          '删除科室',
          { type: 'warning' }
        )
      } catch (e) {
        return
      }
      try {
        await departmentApi.deleteDept(row.id)
        Message.success('删除成功')
        this.fetchList()
      } catch (e) {
        const err = e.response && e.response.data
        const msg = (err && (err.detail || err.non_field_errors)) || '删除失败'
        Message.error(Array.isArray(msg) ? msg.join('；') : msg)
      }
    },
    formatTime(v) {
      if (!v) return '-'
      return String(v).slice(0, 19).replace('T', ' ')
    },
  },
}
</script>

<style scoped>
.department-page {
  padding: 16px;
  /* 保险：绝不允许被任何全局规则锁死成 100vh+flex——否则 el-table 会被压扁，
     超出行数被 root 的 overflow:hidden 无声裁掉（表现为「共 30 条只显示 13 行」） */
  display: block;
  height: auto;
  box-sizing: border-box;
}
.search-form {
  margin-bottom: 8px;
}
.toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}
.toolbar-tip {
  color: #909399;
  font-size: 12px;
}
.dept-table {
  width: 100%;
}
.danger-link {
  color: #f56c6c;
}
.pager {
  margin-top: 12px;
  text-align: right;
}
.contact-eye {
  padding: 0;
  margin-left: 6px;
  color: #409eff;
}
.rename-alert {
  margin-bottom: 12px;
}
.table-empty {
  color: #909399;
  font-size: 13px;
  padding: 16px 0;
}
</style>
